import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { motion, useDragControls, useIsPresent, useMotionValue, type Target } from "framer-motion";

import { VibeChat } from "@/components/VibeChat";
import { FloatingWidgetChartLayoutProvider } from "@/components/VibeChat/widgets/FloatingWidget/FloatingWidgetChartLayoutContext";
import { useContentAreaRect } from "@/components/VibeChat/widgets/FloatingWidget/hooks/useContentAreaCenter";
import { useDeferChartsDuringLayoutChange } from "@/components/VibeChat/widgets/FloatingWidget/hooks/useDeferChartsDuringLayoutChange";
import {
  DEFAULT_PANEL_POSITION,
  FLOATING_WIDGET_LAYOUT_ID,
  PANEL_BORDER_RADIUS,
  PANEL_DEFAULT_BOTTOM,
  PANEL_DEFAULT_RIGHT,
  PANEL_RESIZE_HANDLE_SIZE,
  PANEL_RESIZE_ICON_SIZE,
  PANEL_VIEWPORT_PADDING,
  clampPanelSize,
  floatingWidgetExitTransition,
  floatingWidgetMorphSpring,
  getDefaultPanelSize,
  type PanelSize,
  type PanelPosition,
} from "@/components/VibeChat/widgets/FloatingWidget/panel-constants";
import useSessionStorageState, { SESSION_STORAGE_KEYS } from "@/hooks/useSessionStorageState";

interface FloatingPanelProps {
  isFullscreen?: boolean;
  panelPosition: PanelPosition;
  setPanelPosition: (position: PanelPosition) => void;
  pendingPrompt?: string | null;
  shouldPendingPromptStartNewChat?: boolean;
  onPendingPromptConsumed?: () => void;
  onClose: () => void;
  onToggleSize?: () => void;
}

const computeBounds = (panelWidth: number, panelHeight: number) => ({
  left: -(window.innerWidth - panelWidth - PANEL_DEFAULT_RIGHT - PANEL_VIEWPORT_PADDING),
  right: PANEL_DEFAULT_RIGHT - PANEL_VIEWPORT_PADDING,
  top: -(window.innerHeight - PANEL_DEFAULT_BOTTOM - panelHeight - PANEL_VIEWPORT_PADDING),
  bottom: PANEL_DEFAULT_BOTTOM - PANEL_VIEWPORT_PADDING,
});

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const getFloatingFrame = (panelSize: PanelSize): Target => ({
  left: window.innerWidth - PANEL_DEFAULT_RIGHT - panelSize.width,
  top: window.innerHeight - PANEL_DEFAULT_BOTTOM - panelSize.height,
  width: panelSize.width,
  height: panelSize.height,
});

const INTERACTIVE_SELECTOR =
  "button, a, input, textarea, select, [role='button'], [role='menuitem'], [data-no-drag]";

export const FloatingPanel = ({
  isFullscreen = false,
  panelPosition,
  setPanelPosition,
  pendingPrompt,
  shouldPendingPromptStartNewChat,
  onPendingPromptConsumed,
  onClose,
  onToggleSize,
}: FloatingPanelProps) => {
  const [savedSize, setSavedSize] = useSessionStorageState<PanelSize>(
    SESSION_STORAGE_KEYS.VIBE_PANEL_SIZE,
    getDefaultPanelSize(),
  );
  const savedPosition = panelPosition;
  const panelSize = clampPanelSize(savedSize);
  const [bounds, setBounds] = useState(() => computeBounds(panelSize.width, panelSize.height));
  const [isResizing, setIsResizing] = useState(false);
  const dragControls = useDragControls();
  const isPresent = useIsPresent();
  const contentBounds = useContentAreaRect();
  const displayedFullscreenRef = useRef(isFullscreen);
  if (isPresent) {
    displayedFullscreenRef.current = isFullscreen;
  }
  const displayedFullscreen = displayedFullscreenRef.current;
  const x = useMotionValue(clamp(savedPosition.x, bounds.left, bounds.right));
  const y = useMotionValue(clamp(savedPosition.y, bounds.top, bounds.bottom));

  const floatingFrame = useMemo(() => getFloatingFrame(panelSize), [panelSize]);

  const fullscreenFrame = useMemo((): Target => {
    if (!contentBounds) {
      return floatingFrame;
    }

    return {
      left: contentBounds.left + 1,
      top: contentBounds.top,
      width: contentBounds.width - 1,
      height: contentBounds.height,
    };
  }, [contentBounds, floatingFrame]);

  const shouldDeferCharts = useDeferChartsDuringLayoutChange({
    contentBounds,
    isFullscreen,
    isResizing,
  });

  const frameTransition = isResizing ? { duration: 0 } : floatingWidgetMorphSpring;

  useEffect(() => {
    const reclamp = (next: ReturnType<typeof computeBounds>) => {
      const cx = clamp(x.get(), next.left, next.right);
      const cy = clamp(y.get(), next.top, next.bottom);
      if (cx !== x.get() || cy !== y.get()) {
        x.set(cx);
        y.set(cy);
        setPanelPosition({ x: Math.round(cx), y: Math.round(cy) });
      }
    };

    const recompute = () => {
      const next = computeBounds(panelSize.width, panelSize.height);
      setBounds(next);
      reclamp(next);
    };

    recompute();
    window.addEventListener("resize", recompute);

    return () => {
      window.removeEventListener("resize", recompute);
    };
  }, [panelSize.height, panelSize.width, setPanelPosition, x, y]);

  useEffect(() => {
    x.set(clamp(savedPosition.x, bounds.left, bounds.right));
    y.set(clamp(savedPosition.y, bounds.top, bounds.bottom));
  }, [
    bounds.bottom,
    bounds.left,
    bounds.right,
    bounds.top,
    savedPosition.x,
    savedPosition.y,
    x,
    y,
  ]);

  const handlePointerDownCapture = (event: ReactPointerEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (!target.closest("[data-panel-drag-handle]")) {
      return;
    }
    if (target.closest(INTERACTIVE_SELECTOR)) {
      return;
    }
    dragControls.start(event);
  };

  const handleResizePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const resizeHandle = event.currentTarget;
      resizeHandle.setPointerCapture(event.pointerId);
      setIsResizing(true);

      const startX = event.clientX;
      const startY = event.clientY;
      const startWidth = panelSize.width;
      const startHeight = panelSize.height;

      const handlePointerMove = (moveEvent: PointerEvent) => {
        const nextSize = clampPanelSize({
          width: startWidth + (startX - moveEvent.clientX),
          height: startHeight + (startY - moveEvent.clientY),
        });
        setSavedSize(nextSize);
      };

      const handlePointerUp = (upEvent: PointerEvent) => {
        resizeHandle.releasePointerCapture(upEvent.pointerId);
        resizeHandle.removeEventListener("pointermove", handlePointerMove);
        resizeHandle.removeEventListener("pointerup", handlePointerUp);
        setIsResizing(false);
      };

      resizeHandle.addEventListener("pointermove", handlePointerMove);
      resizeHandle.addEventListener("pointerup", handlePointerUp);
    },
    [panelSize.height, panelSize.width, setSavedSize],
  );

  const handleResetLayout = useCallback(() => {
    const defaultSize = getDefaultPanelSize();
    setPanelPosition(DEFAULT_PANEL_POSITION);
    setSavedSize(defaultSize);
    x.set(DEFAULT_PANEL_POSITION.x);
    y.set(DEFAULT_PANEL_POSITION.y);
    setBounds(computeBounds(defaultSize.width, defaultSize.height));
  }, [setPanelPosition, setSavedSize, x, y]);

  return (
    <motion.div
      className={displayedFullscreen ? "fixed z-20" : "fixed z-50"}
      initial={false}
      animate={displayedFullscreen ? fullscreenFrame : floatingFrame}
      transition={frameTransition}
      drag={!displayedFullscreen}
      dragListener={false}
      dragControls={dragControls}
      dragConstraints={bounds}
      dragElastic={0.1}
      dragMomentum={false}
      onDragEnd={() => {
        if (displayedFullscreen) {
          return;
        }
        setPanelPosition({ x: Math.round(x.get()), y: Math.round(y.get()) });
      }}
      onPointerDownCapture={handlePointerDownCapture}
      style={{
        pointerEvents: isPresent ? "auto" : "none",
        x: displayedFullscreen ? 0 : x,
        y: displayedFullscreen ? 0 : y,
      }}
    >
      <motion.div
        layoutId={displayedFullscreen ? undefined : FLOATING_WIDGET_LAYOUT_ID}
        layout={!displayedFullscreen && !isResizing}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: floatingWidgetExitTransition }}
        transition={displayedFullscreen || isResizing ? { duration: 0 } : floatingWidgetMorphSpring}
        className={`relative flex h-full min-h-0 w-full flex-col overflow-hidden border border-gray-200 bg-white ${displayedFullscreen ? "border-none shadow-none" : "shadow-2xl"}`}
        style={{ borderRadius: displayedFullscreen ? 0 : PANEL_BORDER_RADIUS }}
      >
        <div className="flex min-h-0 flex-1 flex-col">
          {isPresent ? (
            <FloatingWidgetChartLayoutProvider
              isChatFullscreen={isFullscreen}
              shouldRenderCharts={!shouldDeferCharts}
            >
              <VibeChat
                onClose={onClose}
                onToggleSize={onToggleSize}
                onResetPanelLayout={handleResetLayout}
                isMaximized={isFullscreen}
                initialPrompt={pendingPrompt}
                shouldInitialPromptStartNewChat={shouldPendingPromptStartNewChat}
                onInitialPromptConsumed={onPendingPromptConsumed}
              />
            </FloatingWidgetChartLayoutProvider>
          ) : null}
        </div>

        {!displayedFullscreen && (
          <div
            data-no-drag
            aria-hidden
            className="group absolute left-0 top-0 z-10 flex cursor-nwse-resize items-start justify-start p-1"
            style={{
              height: PANEL_RESIZE_HANDLE_SIZE,
              width: PANEL_RESIZE_HANDLE_SIZE,
            }}
            onPointerDown={handleResizePointerDown}
          >
            <svg
              width={PANEL_RESIZE_ICON_SIZE}
              height={PANEL_RESIZE_ICON_SIZE}
              viewBox={`0 0 ${PANEL_RESIZE_ICON_SIZE} ${PANEL_RESIZE_ICON_SIZE}`}
              fill="none"
              className="text-gray-300 transition-colors group-hover:text-gray-500"
            >
              <path
                d="M8.5 1.5L1.5 8.5M4.5 1.5L1.5 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};
